// src/components/command/CommandPalette.tsx

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Users,
  CheckSquare,
  TrendingUp,
  Mail,
  Radio,
  ShoppingBag,
  Calendar,
  Folder,
  File,
  Clipboard,
  Settings,
  UserPlus,
  PlusCircle,
  Target,
  RefreshCw,
  CreditCard,
  Activity,
  Link2,
  User,
  MessageCircle,
  Cpu,
  List,
  Bell,
  Search,
  GitBranch,
  Briefcase,
  Circle,
  type LucideIcon,
} from 'lucide-react';
import { useCommandEngine, CommandEngineItem } from '../../command/useCommandEngine';
import LoadingSpinner from '../common/LoadingSpinner';

// ============================================================
// Icon Map - Maps icon names to Lucide components
// ============================================================

const iconMap: Record<string, LucideIcon> = {
  'users': Users,
  'check-square': CheckSquare,
  'trending-up': TrendingUp,
  'mail': Mail,
  'radio': Radio,
  'shopping-bag': ShoppingBag,
  'calendar': Calendar,
  'folder': Folder,
  'file': File,
  'clipboard': Clipboard,
  'settings': Settings,
  'user-plus': UserPlus,
  'plus-square': PlusCircle,
  'target': Target,
  'repeat': RefreshCw,
  'credit-card': CreditCard,
  'activity': Activity,
  'link': Link2,
  'user': User,
  'message-circle': MessageCircle,
  'cpu': Cpu,
  'list': List,
  'bell': Bell,
  'search': Search,
  'git-branch': GitBranch,
  'briefcase': Briefcase,
  'circle': Circle,
};

interface IconProps {
  name: string;
  className?: string;
}

function Icon({ name, className = 'w-4 h-4' }: IconProps) {
  const IconComponent = iconMap[name] || Circle;
  return <IconComponent className={className} />;
}

// ============================================================
// Result Type Badge
// ============================================================

interface ResultTypeBadgeProps {
  type: string;
}

function ResultTypeBadge({ type }: ResultTypeBadgeProps) {
  const typeLabels: Record<string, string> = {
    contact: 'Contact',
    task: 'Task',
    opportunity: 'Deal',
    pipeline: 'Pipeline',
    event: 'Event',
    file: 'File',
    form: 'Form',
    project: 'Project',
    message: 'Message',
    channel: 'Channel',
    team_member: 'Team',
    workspace: 'Workspace',
  };

  return (
    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
      {typeLabels[type] || type}
    </span>
  );
}

// ============================================================
// Command Item Component
// ============================================================

interface CommandItemProps {
  item: CommandEngineItem;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}

function CommandItem({ item, isSelected, onClick, onMouseEnter }: CommandItemProps) {
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isSelected]);

  return (
    <div
      ref={itemRef}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={`
        flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors
        ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}
      `}
    >
      <div className={`flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`}>
        <Icon name={item.icon || 'circle'} className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
            {item.label}
          </span>
          {item.type === 'result' && item.resultType && (
            <ResultTypeBadge type={item.resultType} />
          )}
        </div>
        {item.subtitle && (
          <p className="text-sm text-gray-500 truncate">{item.subtitle}</p>
        )}
      </div>
      {isSelected && (
        <div className="flex-shrink-0 text-gray-400">
          <span className="text-xs">Enter</span>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Command Palette Component
// ============================================================

export default function CommandPalette() {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    isOpen,
    query,
    selectedIndex,
    isLoading,
    commands,
    searchResults,
    allItems,
    groupedCommands,
    categoryLabels,
    close,
    setQuery,
    selectIndex,
    executeItem,
  } = useCommandEngine();

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const hasQuery = query.trim().length > 0;
  const showSearchResults = hasQuery && searchResults.length > 0;
  const showCommands = commands.length > 0;
  const hasResults = showCommands || showSearchResults;

  // Calculate item index for selection tracking
  let currentIndex = 0;
  const getItemIndex = () => currentIndex++;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={close}
      />

      {/* Dialog */}
      <div className="flex min-h-full items-start justify-center p-4 pt-[15vh]">
        <div className="relative w-full max-w-xl transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all">
          {/* Search Input */}
          <div className="flex items-center gap-3 border-b border-gray-200 px-4">
            <Icon name="search" className="w-5 h-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search or type a command..."
              className="flex-1 py-4 text-lg placeholder-gray-400 focus:outline-none"
            />
            {isLoading && <LoadingSpinner size="sm" />}
            <kbd className="hidden sm:inline-flex px-2 py-1 text-xs font-medium text-gray-400 bg-gray-100 rounded">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {!hasResults && hasQuery && !isLoading && (
              <div className="px-4 py-8 text-center text-gray-500">
                No results found for "{query}"
              </div>
            )}

            {!hasResults && !hasQuery && (
              <div className="px-4 py-8 text-center text-gray-500">
                Start typing to search...
              </div>
            )}

            {/* Commands Section */}
            {showCommands && (
              <div>
                {hasQuery ? (
                  // When searching, show flat list
                  <div>
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                      Commands
                    </div>
                    {commands.map((item) => {
                      const idx = getItemIndex();
                      return (
                        <CommandItem
                          key={item.id}
                          item={item}
                          isSelected={selectedIndex === idx}
                          onClick={() => executeItem(item)}
                          onMouseEnter={() => selectIndex(idx)}
                        />
                      );
                    })}
                  </div>
                ) : (
                  // When not searching, show grouped by category
                  Object.entries(groupedCommands).map(([category, items]) => (
                    <div key={category}>
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                        {categoryLabels[category as keyof typeof categoryLabels] || category}
                      </div>
                      {items.map((item) => {
                        const idx = getItemIndex();
                        return (
                          <CommandItem
                            key={item.id}
                            item={item}
                            isSelected={selectedIndex === idx}
                            onClick={() => executeItem(item)}
                            onMouseEnter={() => selectIndex(idx)}
                          />
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Search Results Section */}
            {showSearchResults && (
              <div>
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                  Search Results
                </div>
                {searchResults.map((item) => {
                  const idx = getItemIndex();
                  return (
                    <CommandItem
                      key={item.id}
                      item={item}
                      isSelected={selectedIndex === idx}
                      onClick={() => executeItem(item)}
                      onMouseEnter={() => selectIndex(idx)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-2 text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">↓</kbd>
                to navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">↵</kbd>
                to select
              </span>
            </div>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">⌘</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">K</kbd>
              to toggle
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
